/**
 * The credential fields an exchange row asks the user for.
 *
 * Kept apart from useGetExchange so it can be reasoned about (and tested)
 * without dragging in react-query, axios and auth0 through that hook's imports.
 */
export enum ExchangeApiRequiredField {
  ACCOUNT_ID = "account_id",
  KEY = "key",
  SECRET = "secret",
  PASSWORD = "password",
  BLOCKCHAIN = "blockchain",
}

/**
 * The exchange catalogue spells the same credential several ways: 88 of the 537
 * rows say `api_key`/`api_secret` rather than `key`/`secret`, including live
 * venues (gemini, luno, bitstamp, mxc, binance-us, wazirx). Anything the enum
 * does not recognise is silently dropped by the connect form, which then renders
 * with no credential fields at all and no way to connect.
 *
 * iOS already tolerates the aliases (FormFieldConfig in MoonscapeTypes.swift);
 * this is the same mapping, so the two clients agree on what a row is asking for.
 */
const FIELD_ALIASES: Record<string, ExchangeApiRequiredField> = {
  key: ExchangeApiRequiredField.KEY,
  apikey: ExchangeApiRequiredField.KEY,
  api_key: ExchangeApiRequiredField.KEY,
  secret: ExchangeApiRequiredField.SECRET,
  apisecret: ExchangeApiRequiredField.SECRET,
  api_secret: ExchangeApiRequiredField.SECRET,
  password: ExchangeApiRequiredField.PASSWORD,
  passphrase: ExchangeApiRequiredField.PASSWORD,
  account_id: ExchangeApiRequiredField.ACCOUNT_ID,
  accountid: ExchangeApiRequiredField.ACCOUNT_ID,
  blockchain: ExchangeApiRequiredField.BLOCKCHAIN,
};

/** The catalogue's field names as the form's own field names. Unknown names are
 * dropped, as before -- there is no input to render for a credential we cannot
 * place, and guessing one would ask the user for the wrong secret. */
export const normalizeApiRequiredFields = (
  fields: readonly string[] | null | undefined
): ExchangeApiRequiredField[] => [
  ...new Set(
    (fields ?? [])
      .map((field) => FIELD_ALIASES[String(field).trim().toLowerCase()])
      .filter((field): field is ExchangeApiRequiredField => Boolean(field))
  ),
];
